// src/context/ProsContext.jsx
import { createContext, useReducer, useEffect, useContext, useCallback, useMemo } from "react";
import { AuthContext } from "./AuthContext";
import { createProsInteraction, fetchProsInteraction, generateJobSynthesis } from "../api/prosInteractions";

function reducerProsInteraction(state = {}, action) {
  const { type, ciaov2_job_id, name, gender, title, survey_synthesis } = action;
  switch (type) {
    case "INIT_SESSION":
      return state[ciaov2_job_id]
        ? state
        : { ...state, [ciaov2_job_id]: { name, gender, title, survey_synthesis } };
    case "UPDATE_SYNTHESIS":
      return state[ciaov2_job_id]
        ? { ...state, [ciaov2_job_id]: { ...state[ciaov2_job_id], survey_synthesis } }
        : state;
    case "RESET_SESSION":
      return {}
    default:
      return state;
  }
}

export const ProsContext = createContext({
  itemsPro: {},
  setItemsPro: async () => {},
  updateSynthesis: async () => {},
});

function initProsInteraction() {
  return {};
}

export function ProsProvider({ children }) {
  const [itemsPro, dispatchProsInteraction] = useReducer(reducerProsInteraction, {}, initProsInteraction);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!user?.id) {
      resetSession()
    } else {
      (async () => {
        try {
          const prosInteractions = await fetchProsInteraction(user.authToken)
          prosInteractions.forEach((prosInteraction) => {
            dispatchProsInteraction({
              type: "INIT_SESSION",
              ciaov2_job_id: prosInteraction.ciaov2_job_id,
              name: prosInteraction.name,
              gender: prosInteraction.gender,
              title: prosInteraction.gender === 'boy' ? prosInteraction.job.title_m : prosInteraction.job.title_f,
              survey_synthesis: prosInteraction.survey_synthesis
            });
          })
        } catch (err) {
          console.error("initSession failed:", err);
        }
      })();
    }
  }, [user?.id]);

  const resetSession = useCallback(
    async () => {
      dispatchProsInteraction({
        type: "RESET_SESSION"
      })
    }
  )

  // action pour initialiser la session vide
  const setItemsPro = useCallback(
    async (value) => {
      if (itemsPro[value.ciaov2_job_id])
        return

      dispatchProsInteraction({
        type: "INIT_SESSION",
        ciaov2_job_id: value.ciaov2_job_id,
        name: value.name,
        gender: value.gender,
        title: value.title,
        survey_synthesis: null
      });

      await createProsInteraction(
        {
          ciaov2_job_id: value.ciaov2_job_id,
          name: value.name,
          gender: value.gender,
          ciaov2_user_id: user.id,
        },
        user.authToken
      );
    }, [itemsPro, user]
  );

  // action pour générer et mettre à jour la synthèse
  const updateSynthesis = useCallback(
    async (jobId) => {
      try {
        const updatedProsInteraction = await generateJobSynthesis(jobId, user.authToken);
        dispatchProsInteraction({
          type: "UPDATE_SYNTHESIS",
          ciaov2_job_id: jobId,
          survey_synthesis: updatedProsInteraction.survey_synthesis
        });
        return updatedProsInteraction.survey_synthesis;
      } catch (err) {
        console.error("updateSynthesis failed:", err);
        throw err;
      }
    }, [user]
  );

  const value = useMemo(() => ({ itemsPro, setItemsPro, updateSynthesis }), [itemsPro, setItemsPro, updateSynthesis]);

  return (
    <ProsContext.Provider value={value}>
      {children}
    </ProsContext.Provider>
  );
}
